import React, { useState, useRef, useEffect, useId } from 'react'
import { useI18n } from '../../i18n/I18nContext'
import { ChevronDown, Globe } from 'lucide-react'
import type { LanguageCode } from '../../types'

// Native names stay recognizable even when the current interface language is unfamiliar.
const languageOptions: { code: LanguageCode; name: string }[] = [
    { code: 'ha', name: 'Hausa' },
    { code: 'ar', name: 'العربية' },
    { code: 'en', name: 'English' },
]

export const LanguageSwitcher: React.FC<{ forceLight?: boolean; forceDark?: boolean; variant?: 'light' | 'dark'; compactMobile?: boolean }> = ({
    forceLight = false,
    forceDark = false,
    variant = 'light',
    compactMobile = false,
}) => {
    const { language, setLanguage, t } = useI18n()
    const [isOpen, setIsOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const optionRefs = useRef<(HTMLButtonElement | null)[]>([])
    const menuId = useId()
    const currentIndex = Math.max(0, languageOptions.findIndex(option => option.code === language))
    const currentLanguage = languageOptions[currentIndex]
    const onDark = forceDark || (!forceLight && variant === 'dark')
    // One border for every control, both themes. Only an always-dark surface (forceDark)
    // keeps a literal edge, because the theme token would not follow that surface.
    const edge = forceDark ? 'border-white/20' : 'border-[var(--dhtn-border)]'
    // Hover wears --dhtn-hover on a theme surface. Only a pinned surface keeps a literal: white
    // on the always-dark one, the light hover value on the always-light one.
    const hoverBg = forceDark ? 'hover:bg-white/10' : forceLight ? 'hover:bg-[#F0F3FC]' : 'hover:bg-[var(--dhtn-hover)]'
    const itemHoverBg = forceDark ? 'hover:bg-white/5' : hoverBg
    const label = t('settings.interfaceLanguage')

    useEffect(() => {
        if (isOpen) optionRefs.current[activeIndex]?.focus()
    }, [isOpen, activeIndex])

    useEffect(() => {
        if (!isOpen) return
        const closeOutside = (event: PointerEvent) => {
            if (event.target instanceof Node && !dropdownRef.current?.contains(event.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('pointerdown', closeOutside)
        return () => document.removeEventListener('pointerdown', closeOutside)
    }, [isOpen])

    const openAt = (index: number) => {
        setActiveIndex(index)
        setIsOpen(true)
    }
    const closeAndFocus = () => {
        setIsOpen(false)
        triggerRef.current?.focus()
    }
    const handleMenuKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Tab' && event.shiftKey) {
            event.preventDefault()
            closeAndFocus()
        } else if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            closeAndFocus()
        } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            event.preventDefault()
            const next = event.key === 'Home' ? 0
                : event.key === 'End' ? languageOptions.length - 1
                : (activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + languageOptions.length) % languageOptions.length
            setActiveIndex(next)
        }
    }

    return (
        <div
            className="relative"
            ref={dropdownRef}
            onBlur={event => {
                if (!event.currentTarget.contains(event.relatedTarget)) setIsOpen(false)
            }}
        >
            <button
                ref={triggerRef}
                type="button"
                onClick={() => isOpen ? setIsOpen(false) : openAt(currentIndex)}
                onKeyDown={event => {
                    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                        event.preventDefault()
                        openAt(event.key === 'ArrowDown' ? 0 : languageOptions.length - 1)
                    }
                }}
                className={`flex min-h-[44px] items-center gap-2.5 rounded-control border-2 ${compactMobile ? 'px-2 sm:px-3' : 'px-3'} text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${edge} ${
                    forceDark ? '' : 'hover:border-[var(--dhtn-border-strong)]'
                } ${
                    onDark
                        ? `text-white ${hoverBg} focus-visible:ring-white focus-visible:ring-offset-[#0a0f1a]`
                        : `text-[#0E1014] ${hoverBg} focus-visible:ring-[#0B5CFF] focus-visible:ring-offset-white`
                }`}
                aria-label={`${label}: ${currentLanguage.name}`}
                aria-haspopup="menu"
                aria-controls={isOpen ? menuId : undefined}
                aria-expanded={isOpen}
            >
                <Globe size={18} strokeWidth={1.75} aria-hidden="true" className={compactMobile ? 'hidden sm:block' : undefined} />
                <span lang={currentLanguage.code} dir={currentLanguage.code === 'ar' ? 'rtl' : 'ltr'}>{currentLanguage.name}</span>
                <ChevronDown
                    size={16}
                    strokeWidth={1.75}
                    aria-hidden="true"
                    className={`${compactMobile ? 'hidden sm:block' : ''} ms-0.5 opacity-70 transition-transform duration-200 motion-reduce:transition-none ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {isOpen && (
                <div
                    id={menuId}
                    role="menu"
                    aria-label={label}
                    onKeyDown={handleMenuKey}
                    className={`absolute top-full end-0 z-50 mt-2 w-52 rounded-card border-2 p-1.5 ${edge} ${
                        onDark ? 'bg-[#0a0f1a]' : 'bg-white'
                    }`}
                >
                    {languageOptions.map((option, index) => {
                        const selected = option.code === language
                        return (
                            <button
                                key={option.code}
                                ref={node => { optionRefs.current[index] = node }}
                                type="button"
                                role="menuitemradio"
                                aria-checked={selected}
                                tabIndex={index === activeIndex ? 0 : -1}
                                onFocus={() => setActiveIndex(index)}
                                onClick={() => {
                                    setLanguage(option.code)
                                    closeAndFocus()
                                }}
                                className={`flex min-h-[48px] w-full items-center justify-between gap-4 rounded-control px-3 text-start text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset ${
                                    onDark
                                        ? `text-white focus-visible:ring-white ${selected ? 'bg-white/10 font-semibold' : itemHoverBg}`
                                        : `text-[#0E1014] focus-visible:ring-[#0B5CFF] ${selected ? 'bg-[#F0F5FE] font-semibold' : itemHoverBg}`
                                }`}
                            >
                                <span lang={option.code} dir={option.code === 'ar' ? 'rtl' : 'ltr'}>{option.name}</span>
                                <svg
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    aria-hidden="true"
                                    className={`h-5 w-5 shrink-0 ${selected ? '' : 'invisible'} ${onDark ? 'text-[#3E7DFF]' : 'text-[#0B5CFF]'}`}
                                >
                                    <path d="m4 10 4 4 8-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
